import { useNavigate } from 'react-router-dom';
import { useFacilities, useSelectFacility } from '@/api/facilities';
import { useAuthStore } from '@/stores/auth-store';
import { toast } from '@/components/ui';
import { t } from '@/i18n';

/**
 * Üst bardaki aktif tesis seçici (docs/09 §1.2).
 *
 * Bir satın alma sorumlusu birden fazla fabrikadan sorumlu olabiliyor; seçilen tesis
 * ilanlara olan uzaklığın ve yakınlık sıralamasının başlangıç noktası.
 *
 * Yalnız alıcı yetkisi olan firmalara gösterilir: satıcının tesis kavramı yok, ilanın
 * konumu ilanın kendi alanı.
 */
export function FacilitySwitcher() {
  const company = useAuthStore((state) => state.company);
  const canBuy = company?.type === 'BUYER' || company?.type === 'BOTH';

  const facilities = useFacilities(canBuy);
  const selectFacility = useSelectFacility();
  const navigate = useNavigate();

  if (!canBuy) return null;

  // Veri gelmeden hiçbir şey göstermiyoruz: aksi hâlde tesisi OLAN kullanıcı da her sayfa
  // açılışında bir an "tesis ekleyin" çağrısını görüyor.
  if (!facilities.data) return null;

  const items = facilities.data;
  const active = items.find((facility) => facility.isDefault);

  // Hiç tesis yoksa seçici yerine ekleme çağrısı gösteriyoruz: mesafe için şart.
  if (items.length === 0) {
    return (
      <button
        type="button"
        onClick={() => navigate('/panel/tesisler')}
        className="hidden rounded-xl border border-dashed border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-primary-700 hover:text-primary-900 md:block"
      >
        {t('facilities.switcherEmpty')}
      </button>
    );
  }

  async function handleChange(id: string) {
    if (id === active?.id) return;
    try {
      await selectFacility.mutateAsync(id);
      toast.success(t('facilities.selected'));
    } catch {
      toast.error(t('errors.unexpected'));
    }
  }

  return (
    <label className="hidden md:block">
      <span className="sr-only">{t('facilities.switcherLabel')}</span>
      <select
        value={active?.id ?? ''}
        disabled={selectFacility.isPending}
        onChange={(event) => void handleChange(event.target.value)}
        className="max-w-[12rem] rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 disabled:opacity-60"
      >
        {items.map((facility) => (
          <option key={facility.id} value={facility.id}>
            {facility.name} · {facility.district.name}
          </option>
        ))}
      </select>
    </label>
  );
}
